import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams, useSearch } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormattedNumberInput } from "@/components/formatted-number-input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Upload, X, GripVertical, Loader2, ShoppingBag, Phone, Mail, PackageSearch } from "lucide-react";
import type { MarketplaceListingWithDetails, InventoryItem, MarketplaceCondition, User, MarketplaceProfile } from "@shared/schema";

type FormattedPriceInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max" | "prefix"> & {
  value: number | null | undefined;
  onChange: (val: number | null) => void;
};

// Thin wrapper over the shared FormattedNumberInput: whole-dollar prices only.
function FormattedPriceInput({ value, onChange, ...props }: FormattedPriceInputProps) {
  return (
    <FormattedNumberInput
      {...props}
      value={value ?? null}
      decimals={0}
      allowNegative={false}
      pattern="[0-9,]*"
      onValueChange={(_raw, num) => onChange(num)}
    />
  );
}

const BODY_STYLES = ["Sedan", "SUV", "Truck", "Coupe", "Convertible", "Wagon", "Van", "Hatchback", "Minivan", "Other"];

function mapNhtsaBodyStyle(raw: string | null | undefined): string {
  if (!raw) return "";
  const s = raw.toLowerCase();
  if (s.includes("sport utility") || s === "suv") return "SUV";
  if (s.includes("pickup") || s.includes("truck")) return "Truck";
  if (s.includes("minivan") || s.includes("mini van")) return "Minivan";
  if (s.includes("van")) return "Van";
  if (s.includes("convertible") || s.includes("cabriolet") || s.includes("roadster")) return "Convertible";
  if (s.includes("coupe")) return "Coupe";
  if (s.includes("hatchback")) return "Hatchback";
  if (s.includes("wagon") || s.includes("estate")) return "Wagon";
  if (s.includes("sedan") || s.includes("saloon")) return "Sedan";
  const exact = BODY_STYLES.find((bs) => bs.toLowerCase() === s);
  return exact ?? "";
}
const CONDITIONS: MarketplaceCondition[] = ["excellent", "good", "fair", "poor"];
const FROM_CLEARED_KEY = "marketplace-from-cleared";
const STATUSES = ["draft", "active", "pending", "sold"] as const;
type MarketplaceStatus = typeof STATUSES[number];

function makeFormSchema(t: TFunction) {
  return z.object({
    vin: z.string().optional(),
    year: z.coerce.number().min(1900).max(new Date().getFullYear() + 2).optional().nullable(),
    make: z.string().min(1, t("marketplace:formErrors.makeRequired")),
    model: z.string().min(1, t("marketplace:formErrors.modelRequired")),
    trim: z.string().optional(),
    bodyStyle: z.string().optional(),
    color: z.string().optional(),
    mileage: z.coerce.number().min(0).optional().nullable(),
    askingPrice: z.coerce.number({ required_error: t("marketplace:formErrors.askingPriceRequired") }).min(0, t("marketplace:formErrors.askingPriceRequired")),
    description: z.string().optional(),
    reconNotes: z.string().optional(),
    condition: z.enum(["excellent", "good", "fair", "poor"] as const).default("good"),
    status: z.enum(["draft", "active", "pending", "sold"] as const).default("draft"),
    contactPhone: z.string().optional(),
    contactEmail: z.string().optional(),
    showPhone: z.boolean().default(false),
    showEmail: z.boolean().default(false),
  });
}

type FormValues = z.infer<ReturnType<typeof makeFormSchema>>;

interface PhotoItem {
  id?: string;
  photoUrl: string;
  displayOrder: number;
  file?: File;
  uploading?: boolean;
}

function PhotoUploadArea({
  photos,
  onAdd,
  onRemove,
  onReorder,
  disabled,
}: {
  photos: PhotoItem[];
  onAdd: (files: FileList) => void;
  onRemove: (idx: number) => void;
  onReorder: (from: number, to: number) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const dragIdx = useRef<number | null>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      onAdd(e.dataTransfer.files);
    }
  }, [onAdd]);

  function handleDragStart(idx: number) {
    dragIdx.current = idx;
  }

  function handleDragOverItem(e: React.DragEvent, toIdx: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }

  function handleDropItem(e: React.DragEvent, toIdx: number) {
    e.preventDefault();
    e.stopPropagation();
    if (dragIdx.current !== null && dragIdx.current !== toIdx) {
      onReorder(dragIdx.current, toIdx);
    }
    dragIdx.current = null;
  }

  function handleDragEnd() {
    dragIdx.current = null;
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-violet-500/50 hover:bg-violet-500/5 transition-colors cursor-pointer"
        onClick={() => {
          if (disabled) return;
          const inp = document.createElement("input");
          inp.type = "file";
          inp.accept = "image/jpeg,image/png,image/webp";
          inp.multiple = true;
          inp.onchange = () => inp.files && onAdd(inp.files);
          inp.click();
        }}
        data-testid="photo-drop-zone"
      >
        <Upload className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          {t("marketplace:photoUpload.clickOrDrag")}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">{t("marketplace:photoUpload.constraints")}</p>
      </div>

      {/* Photo grid with drag-to-reorder */}
      {photos.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground">{t("marketplace:photoUpload.reorderHint")}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {photos.map((photo, idx) => (
              <div
                key={photo.id ?? photo.photoUrl}
                draggable={!photo.uploading}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOverItem(e, idx)}
                onDrop={(e) => handleDropItem(e, idx)}
                onDragEnd={handleDragEnd}
                className="relative group aspect-square rounded-lg overflow-hidden bg-muted border border-border cursor-grab active:cursor-grabbing"
                data-testid={`photo-item-${idx}`}
              >
                <img
                  src={photo.photoUrl}
                  alt={t("marketplace:photoUpload.photoAlt", { index: idx + 1 })}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                />
                {photo.uploading ? (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                ) : (
                  <>
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">{t("marketplace:photoUpload.hero")}</span>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                      <GripVertical className="w-6 h-6 text-white drop-shadow" />
                    </div>
                    <button
                      type="button"
                      data-testid={`button-remove-photo-${idx}`}
                      onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InventoryPickerDialog({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (item: InventoryItem) => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { data: inventory, isLoading: inventoryLoading } = useQuery<{ data: InventoryItem[] }>({
    queryKey: ["/api/inventory", { limit: 500, status: "active" }],
    queryFn: () =>
      fetch("/api/inventory?limit=500&status=active", { credentials: "include" }).then((r) =>
        r.json()
      ),
    enabled: open,
  });
  const { data: activeSourceIds, isLoading: idsLoading } = useQuery<string[]>({
    queryKey: ["/api/marketplace/active-source-ids"],
    enabled: open,
    staleTime: 30_000,
  });

  const isLoading = inventoryLoading || idsLoading;
  const listedIds = useMemo(() => new Set(activeSourceIds ?? []), [activeSourceIds]);
  const activeItems = inventory?.data?.filter((i) => i.status === "active") ?? [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("marketplace:inventoryPicker.title")}</DialogTitle>
          <DialogDescription>{t("marketplace:inventoryPicker.description")}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 rounded" />)}</div>
        ) : activeItems.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">{t("marketplace:inventoryPicker.noItems")}</p>
        ) : (
          <div className="space-y-2">
            {activeItems.map((item) => {
              const alreadyListed = listedIds.has(String(item.id));
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`inventory-item-${item.id}`}
                  disabled={alreadyListed}
                  onClick={() => { if (!alreadyListed) { onSelect(item); onClose(); } }}
                  title={alreadyListed ? t("marketplace:inventoryPicker.alreadyListedTitle") : undefined}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${alreadyListed ? "border-border bg-muted/30 opacity-50 cursor-not-allowed" : "border-border hover:border-violet-500/40 hover:bg-muted/60 cursor-pointer"}`}
                >
                  <p className="font-medium text-sm">
                    {item.year} {item.make} {item.model} {item.trim ?? ""}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("marketplace:inventoryPicker.vinStock", { vin: item.vin ?? "—", stock: item.stockNumber ?? "—" })}
                    {alreadyListed && <span className="ml-2 text-amber-600 dark:text-amber-400">{t("marketplace:inventoryPicker.alreadyListed")}</span>}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplaceListingFormPage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const formSchema = useMemo(() => makeFormSchema(t), [t]);
  const params = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const fromInventoryId = useMemo(() => {
    const p = new URLSearchParams(search);
    return p.get("from") ?? undefined;
  }, [search]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const isEdit = !!params.id;

  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [showInventoryPicker, setShowInventoryPicker] = useState(false);
  const [vinDecoding, setVinDecoding] = useState(false);
  // Track source inventory ID without polluting form state
  const sourceInventoryIdRef = useRef<string | null>(null);
  // Ensure the marketplace-profile contact defaults are applied to a new listing only once
  const profileContactAppliedRef = useRef(false);
  // Track the label of the last inventory item used to prefill the form
  const [prefillLabel, setPrefillLabel] = useState<string | null>(null);

  const { data: existing, isLoading: existingLoading } = useQuery<MarketplaceListingWithDetails>({
    queryKey: ["/api/marketplace", params.id, "edit"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/${params.id}/edit`);
      return res.json();
    },
    enabled: isEdit,
  });

  const { data: me, isLoading: meLoading } = useQuery<Pick<User, "id">>({
    queryKey: ["/api/auth/me"],
    retry: false,
    enabled: isEdit,
  });

  // Derived ownership state — true only once both queries have resolved and IDs don't match
  const isUnauthorizedEdit = isEdit && !meLoading && !existingLoading && !!me && !!existing && me.id !== existing.sellerUserId;

  // Fire the toast + redirect as a side effect when unauthorized
  useEffect(() => {
    if (!isUnauthorizedEdit) return;
    toast({ title: t("marketplace:formToast.onlyEditOwn"), variant: "destructive" });
    navigate(`/marketplace/${params.id}`);
  }, [isUnauthorizedEdit, navigate, toast, params.id]);

  // Pre-fill from inventory item when ?from= param is set
  const { data: sourceInventoryItem } = useQuery<InventoryItem>({
    queryKey: ["/api/inventory", fromInventoryId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/inventory/${fromInventoryId}`);
      return res.json();
    },
    enabled: !!fromInventoryId && !isEdit,
  });

  // The dealer's universal marketplace contact defaults (phone/email + visibility).
  // Used to pre-fill new listings so contact "just goes to" the one contact they set up.
  const { data: marketplaceProfile } = useQuery<MarketplaceProfile | null>({
    queryKey: ["/api/marketplace/settings/profile"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/settings/profile");
      return res.json();
    },
    enabled: !isEdit,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vin: "",
      year: undefined,
      make: "",
      model: "",
      trim: "",
      bodyStyle: "",
      color: "",
      mileage: undefined,
      askingPrice: 0,
      description: "",
      reconNotes: "",
      condition: "good",
      status: "draft",
      contactPhone: "",
      contactEmail: "",
      showPhone: false,
      showEmail: false,
    },
  });

  // Populate form when editing an existing listing
  useEffect(() => {
    if (existing) {
      const condition: MarketplaceCondition =
        CONDITIONS.includes(existing.condition as MarketplaceCondition)
          ? (existing.condition as MarketplaceCondition)
          : "good";
      const status: MarketplaceStatus =
        STATUSES.includes(existing.status as MarketplaceStatus)
          ? (existing.status as MarketplaceStatus)
          : "draft";

      form.reset({
        vin: existing.vin ?? "",
        year: existing.year ?? undefined,
        make: existing.make ?? "",
        model: existing.model ?? "",
        trim: existing.trim ?? "",
        bodyStyle: existing.bodyStyle ?? "",
        color: existing.color ?? "",
        mileage: existing.mileage ?? undefined,
        askingPrice: existing.askingPrice ? parseFloat(existing.askingPrice) : 0,
        description: existing.description ?? "",
        reconNotes: existing.reconNotes ?? "",
        condition,
        status,
        contactPhone: existing.contactPhone ?? "",
        contactEmail: existing.contactEmail ?? "",
        showPhone: existing.showPhone ?? false,
        showEmail: existing.showEmail ?? false,
      });
      setPhotos(
        existing.photos.map((p) => ({ id: p.id, photoUrl: p.photoUrl, displayOrder: p.displayOrder }))
      );
    }
  }, [existing]);

  // Seed a NEW listing's contact fields from the dealer's universal marketplace profile.
  // Edit mode keeps the listing's own saved contact (handled above). Only fills fields the
  // dealer hasn't already typed into, and never touches the saved profile (per-listing override).
  function applyProfileContactDefaults(p: MarketplaceProfile) {
    const dirty = form.formState.dirtyFields;
    if (!dirty.contactPhone && !form.getValues("contactPhone")) {
      form.setValue("contactPhone", p.phone ?? "");
    }
    if (!dirty.contactEmail && !form.getValues("contactEmail")) {
      form.setValue("contactEmail", p.email ?? "");
    }
    if (!dirty.showPhone) form.setValue("showPhone", p.showPhone);
    if (!dirty.showEmail) form.setValue("showEmail", p.showEmail);
  }

  // Apply the profile contact defaults once for new listings (manual or pushed from inventory).
  // If no profile exists yet, fields stay blank as before.
  useEffect(() => {
    if (isEdit) return;
    if (marketplaceProfile === undefined) return;
    if (profileContactAppliedRef.current) return;
    profileContactAppliedRef.current = true;
    if (marketplaceProfile) applyProfileContactDefaults(marketplaceProfile);
  }, [marketplaceProfile, isEdit]);

  // Auto-pre-fill when coming from inventory via ?from=<id>
  // Skip if the dealer already cleared the badge for this inventory ID in this browser session.
  useEffect(() => {
    if (sourceInventoryItem && !isEdit) {
      const clearedId = sessionStorage.getItem(FROM_CLEARED_KEY);
      if (clearedId === String(sourceInventoryItem.id)) return;
      prefillFromInventory(sourceInventoryItem);
    }
  }, [sourceInventoryItem]);

  function prefillFromInventory(item: InventoryItem) {
    form.setValue("vin", item.vin ?? "", { shouldDirty: true });
    form.setValue("year", item.year ?? undefined, { shouldDirty: true });
    form.setValue("make", item.make ?? "", { shouldDirty: true });
    form.setValue("model", item.model ?? "", { shouldDirty: true });
    form.setValue("trim", item.trim ?? "", { shouldDirty: true });
    const bs = BODY_STYLES.includes(item.bodyStyle ?? "") ? (item.bodyStyle ?? "") : "";
    form.setValue("bodyStyle", bs, { shouldDirty: true });
    form.setValue("color", item.color ?? "", { shouldDirty: true });
    const askingPrice =
      item.salePrice != null ? parseFloat(item.salePrice) :
      item.expectedSalePrice != null ? parseFloat(item.expectedSalePrice) :
      item.purchasePrice != null ? parseFloat(item.purchasePrice) :
      undefined;
    if (askingPrice != null && !isNaN(askingPrice)) form.setValue("askingPrice", askingPrice, { shouldDirty: true });
    sourceInventoryIdRef.current = String(item.id);
    const parts = [item.year, item.make, item.model].filter(Boolean);
    setPrefillLabel(parts.length > 0 ? parts.join(" ") : item.vin ?? t("marketplace:formToast.unknownVehicle"));
  }

  function clearPrefill() {
    if (fromInventoryId) {
      sessionStorage.setItem(FROM_CLEARED_KEY, fromInventoryId);
    }
    form.reset();
    sourceInventoryIdRef.current = null;
    setPrefillLabel(null);
    setPhotos([]);
    // Re-seed the universal contact defaults so clearing the inventory prefill
    // doesn't wipe the dealer's saved phone/email.
    if (!isEdit && marketplaceProfile) applyProfileContactDefaults(marketplaceProfile);
  }

  async function decodeVin(vin: string) {
    const v = vin.trim().toUpperCase();
    if (v.length !== 17) return;
    setVinDecoding(true);
    try {
      const resp = await fetch(`/api/vin-decode?vin=${encodeURIComponent(v)}`, { credentials: "include" });
      if (!resp.ok) {
        const err = await resp.json();
        toast({ title: err.message ?? t("marketplace:formToast.couldNotDecodeVin"), variant: "destructive" });
        return;
      }
      const data = await resp.json();
      if (!data.make && !data.model) {
        toast({ title: t("marketplace:formToast.couldNotDecodeVinCheck"), variant: "destructive" });
        return;
      }
      if (data.year) form.setValue("year", Number(data.year));
      if (data.make) form.setValue("make", data.make);
      if (data.model) form.setValue("model", data.model);
      if (data.trim) form.setValue("trim", data.trim);
      const mappedBodyStyle = mapNhtsaBodyStyle(data.bodyStyle);
      if (mappedBodyStyle) form.setValue("bodyStyle", mappedBodyStyle);
    } catch {
      toast({ title: t("marketplace:formToast.vinDecodeFailed"), variant: "destructive" });
    } finally {
      setVinDecoding(false);
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues & { publish?: boolean }) => {
      const payload = {
        ...values,
        sourceInventoryId: sourceInventoryIdRef.current ?? null,
        status: values.publish ? "active" : values.status,
      };
      if (isEdit) {
        const res = await apiRequest("PATCH", `/api/marketplace/${params.id}`, payload);
        return res.json();
      } else {
        const res = await apiRequest("POST", `/api/marketplace`, payload);
        return res.json();
      }
    },
    onSuccess: async (listing: MarketplaceListingWithDetails) => {
      if (photos.length > 0) {
        const ordered = photos.map((p, i) => ({ photoUrl: p.photoUrl, displayOrder: i }));
        await apiRequest("PATCH", `/api/marketplace/${listing.id}/photos`, { photos: ordered });
      } else if (isEdit) {
        await apiRequest("PATCH", `/api/marketplace/${listing.id}/photos`, { photos: [] });
      }
      qc.invalidateQueries({ queryKey: ["/api/marketplace"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace/active-source-ids"] });
      toast({ title: isEdit ? t("marketplace:formToast.listingUpdated") : t("marketplace:formToast.listingCreated") });
      navigate(`/marketplace/${listing.id}`);
    },
    onError: (err: Error) => toast({ title: err.message || t("marketplace:formToast.saveFailed"), variant: "destructive" }),
  });

  const handlePhotosAdd = async (files: FileList) => {
    const remaining = 20 - photos.length;
    const toAdd = Array.from(files).slice(0, remaining);
    if (toAdd.length === 0) {
      toast({ title: t("marketplace:formToast.photoLimitReached"), description: t("marketplace:formToast.photoLimitDesc"), variant: "destructive" });
      return;
    }

    if (isEdit && params.id) {
      for (const file of toAdd) {
        const preview = URL.createObjectURL(file);
        const tempItem: PhotoItem = { photoUrl: preview, displayOrder: photos.length, uploading: true };
        setPhotos((prev) => [...prev, tempItem]);

        try {
          const fd = new FormData();
          fd.append("photo", file);
          const res = await fetch(`/api/marketplace/${params.id}/photos`, {
            method: "POST",
            body: fd,
            credentials: "include",
          });
          if (!res.ok) throw new Error("Upload failed");
          const uploaded = await res.json() as { id: string; photoUrl: string; displayOrder: number };
          setPhotos((prev) =>
            prev.map((p) =>
              p.photoUrl === preview ? { id: uploaded.id, photoUrl: uploaded.photoUrl, displayOrder: uploaded.displayOrder } : p
            )
          );
        } catch {
          setPhotos((prev) => prev.filter((p) => p.photoUrl !== preview));
          toast({ title: t("marketplace:formToast.photoUploadFailed"), variant: "destructive" });
        }
      }
    } else {
      const newPhotos: PhotoItem[] = toAdd.map((file, i) => ({
        photoUrl: URL.createObjectURL(file),
        displayOrder: photos.length + i,
        file,
      }));
      setPhotos((prev) => [...prev, ...newPhotos]);
    }
  };

  const handleSubmit = async (values: FormValues, publish = false) => {
    if (!isEdit && photos.some((p) => p.file)) {
      const payload = {
        ...values,
        status: publish ? "active" : values.status,
        sourceInventoryId: sourceInventoryIdRef.current ?? null,
      };
      try {
        const createRes = await apiRequest("POST", "/api/marketplace", payload);
        const listing = await createRes.json() as MarketplaceListingWithDetails;

        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          if (!photo.file) continue;
          setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: true } : p));
          try {
            const fd = new FormData();
            fd.append("photo", photo.file);
            const upRes = await fetch(`/api/marketplace/${listing.id}/photos`, {
              method: "POST", body: fd, credentials: "include",
            });
            const uploaded = await upRes.json() as { id: string; photoUrl: string; displayOrder: number };
            setPhotos((prev) => prev.map((p, idx) => idx === i ? { id: uploaded.id, photoUrl: uploaded.photoUrl, displayOrder: uploaded.displayOrder, uploading: false } : p));
          } catch {
            toast({ title: t("marketplace:formToast.photoNumUploadFailed", { num: i + 1 }), variant: "destructive" });
          }
        }
        qc.invalidateQueries({ queryKey: ["/api/marketplace"] });
        qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
        qc.invalidateQueries({ queryKey: ["/api/marketplace/active-source-ids"] });
        toast({ title: t("marketplace:formToast.listingCreated") });
        navigate(`/marketplace/${listing.id}`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t("marketplace:formToast.createFailed");
        toast({ title: msg, variant: "destructive" });
      }
    } else {
      saveMutation.mutate({ ...values, publish });
    }
  };

  if (isEdit && (existingLoading || meLoading || isUnauthorizedEdit)) {
    return <div className="max-w-3xl mx-auto p-6 space-y-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/marketplace")} data-testid="button-back">
          <ArrowLeft className="w-4 h-4 mr-1" />{t("marketplace:form.back")}
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-violet-500" />
            {isEdit ? t("marketplace:form.editListing") : t("marketplace:form.newListing")}
          </h1>
          {!isEdit && <p className="text-sm text-muted-foreground">{t("marketplace:form.newSubtitle")}</p>}
        </div>
        {!isEdit && (
          <div className="flex flex-col items-end gap-1">
            <Button variant="outline" size="sm" onClick={() => setShowInventoryPicker(true)} data-testid="button-pull-from-inventory">
              <PackageSearch className="w-4 h-4 mr-1" />
              {prefillLabel ? t("marketplace:form.changeVehicle") : t("marketplace:form.pullFromInventory")}
            </Button>
            {prefillLabel && (
              <div className="flex items-center gap-1" data-testid="prefill-badge">
                <Badge variant="secondary" className="text-xs font-normal max-w-[220px] truncate">
                  {t("marketplace:form.prefilledFrom", { label: prefillLabel })}
                </Badge>
                <button
                  type="button"
                  onClick={clearPrefill}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={t("marketplace:form.clearPrefill")}
                  data-testid="button-clear-prefill"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => handleSubmit(v))} className="space-y-6">
          {/* Vehicle Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("marketplace:form.vehicleInformation")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="vin" render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    {t("marketplace:form.vin")}
                    {vinDecoding && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                  </FormLabel>
                  <FormControl>
                    <Input
                      data-testid="input-vin"
                      placeholder={t("marketplace:form.vinPlaceholder")}
                      autoComplete="off"
                      {...field}
                      onChange={(e) => {
                        const cleaned = e.target.value.trim().toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/gi, "").slice(0, 17);
                        field.onChange(cleaned);
                        if (cleaned.length === 17) decodeVin(cleaned);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="year" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.year")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input data-testid="input-year" type="number" placeholder={t("marketplace:form.yearPlaceholder")} autoComplete="off" {...field} value={field.value ?? ""} disabled={vinDecoding} />
                        {vinDecoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="make" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.make")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input data-testid="input-make" placeholder={t("marketplace:form.makePlaceholder")} autoComplete="off" {...field} disabled={vinDecoding} />
                        {vinDecoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="model" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.model")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input data-testid="input-model" placeholder={t("marketplace:form.modelPlaceholder")} autoComplete="off" {...field} disabled={vinDecoding} />
                        {vinDecoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="trim" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.trim")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input data-testid="input-trim" placeholder={t("marketplace:form.trimPlaceholder")} autoComplete="off" {...field} disabled={vinDecoding} />
                        {vinDecoding && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="mileage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.mileage")}</FormLabel>
                    <FormControl>
                      <Input data-testid="input-mileage" type="number" placeholder={t("marketplace:form.mileagePlaceholder")} autoComplete="off" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="bodyStyle" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.bodyStyle")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ""}>
                      <FormControl>
                        <SelectTrigger data-testid="select-body-style">
                          <SelectValue placeholder={t("marketplace:form.select")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {BODY_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="color" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.exteriorColor")}</FormLabel>
                    <FormControl>
                      <Input data-testid="input-color" placeholder={t("marketplace:form.colorPlaceholder")} autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Condition */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("marketplace:form.pricingCondition")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField control={form.control} name="askingPrice" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.askingPrice")}</FormLabel>
                    <FormControl>
                      <FormattedPriceInput data-testid="input-asking-price" placeholder={t("marketplace:form.askingPricePlaceholder")} autoComplete="off" value={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="condition" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("marketplace:form.condition")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-condition">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{t(`marketplace:condition.${c}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("marketplace:form.descriptionNotes")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField control={form.control} name="description" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("marketplace:form.generalDescription")}</FormLabel>
                  <FormControl>
                    <Textarea data-testid="input-description" placeholder={t("marketplace:form.descriptionPlaceholder")} rows={3} autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="reconNotes" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("marketplace:form.reconNotes")}</FormLabel>
                  <FormControl>
                    <Textarea data-testid="input-recon-notes" placeholder={t("marketplace:form.reconNotesPlaceholder")} rows={3} autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                {t("marketplace:form.contactInfo")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("marketplace:form.contactHint")}
              </p>

              {/* Phone */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FormField control={form.control} name="showPhone" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          data-testid="checkbox-show-phone"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 cursor-pointer font-normal">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                        {t("marketplace:form.showPhone")}
                      </FormLabel>
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="contactPhone" render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid="input-contact-phone"
                        placeholder={t("marketplace:form.phonePlaceholder")}
                        type="tel"
                        autoComplete="off"
                        {...field}
                        disabled={!form.watch("showPhone")}
                        className={!form.watch("showPhone") ? "opacity-50" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FormField control={form.control} name="showEmail" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          data-testid="checkbox-show-email"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 cursor-pointer font-normal">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        {t("marketplace:form.showEmail")}
                      </FormLabel>
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="contactEmail" render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid="input-contact-email"
                        placeholder={t("marketplace:form.emailPlaceholder")}
                        type="email"
                        autoComplete="off"
                        {...field}
                        disabled={!form.watch("showEmail")}
                        className={!form.watch("showEmail") ? "opacity-50" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

            </CardContent>
          </Card>

          {/* Photos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("marketplace:form.photos", { count: photos.length })}</CardTitle>
            </CardHeader>
            <CardContent>
              <PhotoUploadArea
                photos={photos}
                onAdd={handlePhotosAdd}
                onRemove={(idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                onReorder={(from, to) => {
                  setPhotos((prev) => {
                    const next = [...prev];
                    const [moved] = next.splice(from, 1);
                    next.splice(to, 0, moved);
                    return next.map((p, i) => ({ ...p, displayOrder: i }));
                  });
                }}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="submit"
              variant="outline"
              data-testid="button-save-draft"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {t("marketplace:form.saveDraft")}
            </Button>
            <Button
              type="button"
              data-testid="button-publish"
              className="bg-violet-600 hover:bg-violet-700 text-white"
              disabled={saveMutation.isPending}
              onClick={form.handleSubmit((v) => handleSubmit(v, true))}
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {isEdit ? t("marketplace:form.updatePublish") : t("marketplace:form.publishListing")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/marketplace")} data-testid="button-cancel">
              {t("marketplace:form.cancel")}
            </Button>
          </div>
        </form>
      </Form>

      {/* Inventory picker dialog */}
      <InventoryPickerDialog
        open={showInventoryPicker}
        onClose={() => setShowInventoryPicker(false)}
        onSelect={prefillFromInventory}
      />
    </div>
  );
}
